import { createFileRoute, Link, notFound, redirect, useParams } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect } from "react";
import { ArrowLeft, BookOpen, Clock, Download, Sparkles } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { GUIDES, type Guide } from "@/lib/guides";
import { type Resource } from "@/lib/library-resources";
import { downloadFile } from "@/lib/download-file";
import { type CatalogArticle, type CatalogCategory } from "@/lib/library-catalog";


import { getGeneratedArticle } from "@/lib/library-articles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FavoriteButton } from "@/components/FavoriteButton";
import { markArticleRead } from "@/lib/article-reads";

type LoaderResult =
  | { kind: "guide"; guide: Guide }
  | { kind: "resource"; resource: Resource }
  | { kind: "placeholder"; article: CatalogArticle; category: CatalogCategory };

export const Route = createFileRoute("/library/$slug")({
  head: ({ params, loaderData }) => {
    const url = `https://recoverytrauma.com/library/${params.slug}`;
    const data = loaderData as LoaderResult | undefined;
    if (!data) return { meta: [{ title: "Recovery Library" }] };

    const title =
      data.kind === "guide"
        ? data.guide.title
        : data.kind === "resource"
          ? data.resource.title
          : data.article.title;
    const description =
      data.kind === "guide"
        ? data.guide.blurb
        : data.kind === "resource"
          ? data.resource.blurb
          : `${data.article.title} — part of the ${data.category.label} collection. Content coming soon from the Recovery Trauma™ team.`;

    return {
      meta: [
        { name: "robots", content: "noindex" },
        { title: `${title} — Recovery Library` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            author: { "@type": "Organization", name: "Recovery Trauma\u2122" },
            publisher: { "@type": "Organization", name: "Recovery Trauma\u2122" },
            mainEntityOfPage: url,
          }),
        },
      ],
    };
  },
  // Article text is fetched only when an article page is actually opened,
  // so it never weighs down every other page on the site.
  loader: async ({ params }): Promise<LoaderResult> => {
    const { LIBRARY_SLUG_ALIASES, getCatalogArticle } = await import("@/lib/library-catalog");
    const canonical = LIBRARY_SLUG_ALIASES[params.slug];
    if (canonical) {
      throw redirect({ to: "/library/$slug", params: { slug: canonical }, replace: true });
    }

    const [{ getGuide }, { getResource }] = await Promise.all([
      import("@/lib/guides"),
      import("@/lib/library-resources"),
    ]);

    const guide = getGuide(params.slug);
    if (guide) return { kind: "guide", guide };
    const resource = getResource(params.slug);
    if (resource) return { kind: "resource", resource };
    const placeholder = getCatalogArticle(params.slug);
    if (placeholder) return { kind: "placeholder", ...placeholder };
    throw notFound();
  },

  component: withMemberGate(LibraryArticlePage, "Library Article"),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-display text-3xl text-foreground">Article not found</h1>
      <p className="mt-2 text-muted-foreground">
        The article you're looking for may have moved.
      </p>
      <Link
        to="/library"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Library
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-display text-3xl text-foreground">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : "Please try again."}
      </p>
    </div>
  ),
});

function LibraryArticlePage() {
  const data = Route.useLoaderData() as LoaderResult;
  const { slug } = useParams({ from: "/library/$slug" });
  useEffect(() => {
    markArticleRead(slug);
  }, [slug]);
  if (data.kind === "guide") return <GuideView guide={data.guide} />;
  if (data.kind === "resource") return <ResourceView resource={data.resource} />;
  return <PlaceholderView article={data.article} category={data.category} />;
}

function PlaceholderView({
  article,
  category,
}: {
  article: CatalogArticle;
  category: CatalogCategory;
}) {
  const generated = getGeneratedArticle(article.slug);

  return (
    <article className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/library"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Recovery Library
      </Link>

      <header className="mt-6 rounded-3xl border border-gold/20 bg-gradient-to-br from-card via-card to-gold/5 p-8 sm:p-10">
        <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" />
          <span aria-hidden>{category.emoji}</span>
          <span>{category.label}</span>
        </div>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground sm:text-5xl">
          {article.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Part of our <strong>{category.label}</strong> collection.
        </p>
        <div className="mt-5">
          <FavoriteButton
            storageKey="rt:fav-library"
            id={article.slug}
            label={article.title}
            sub={category.label}
            to={`/library/${article.slug}`}
            variant="pill"
          />
        </div>
      </header>

      {generated ? (
        <div className="prose prose-neutral mt-10 max-w-none dark:prose-invert prose-headings:font-display prose-headings:text-foreground prose-h1:hidden prose-h2:mt-10 prose-h2:text-2xl prose-h3:text-lg prose-p:text-foreground/85 prose-p:leading-relaxed prose-strong:text-foreground prose-li:text-foreground/85 prose-a:text-[var(--gold-deep)]">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {generated.markdown}
          </ReactMarkdown>

          <div className="not-prose mt-10 rounded-2xl border border-gold/20 bg-gold-light/15 p-5 text-sm leading-relaxed text-foreground/80">
            <p className="m-0">
              <strong>Disclaimer.</strong> This article is provided for educational
              purposes only and is not intended to diagnose, treat, or replace
              professional mental health support. If you're struggling, please reach
              out to a qualified therapist or your local crisis service.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-10 rounded-2xl border border-dashed border-gold/40 bg-gold-light/20 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            <BookOpen className="h-3.5 w-3.5" /> Coming soon
          </div>
          <h2 className="mt-3 font-display text-2xl text-foreground">
            Content being written by the Recovery Trauma™ team
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground/80">
            Our team is preparing a trauma-informed guide on <em>{article.title}</em>.
          </p>
        </div>
      )}

      <section className="mt-12">
        <h2 className="font-display text-xl text-foreground">
          More in {category.label}
        </h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {category.articles
            .filter((a) => a.slug !== article.slug)
            .slice(0, 8)
            .map((a) => (
              <li key={a.slug}>
                <Link
                  to="/library/$slug"
                  params={{ slug: a.slug }}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-sm text-foreground hover:border-gold/40 hover:bg-gold-light/20"
                >
                  <span>{a.title}</span>
                  <span className="text-[var(--gold-deep)]">→</span>
                </Link>
              </li>
            ))}
        </ul>
      </section>

      <div className="mt-10">
        <Link
          to="/library"
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-gold/40"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Library
        </Link>
      </div>
    </article>
  );
}

function GuideView({ guide }: { guide: Guide }) {
  const others = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 3);

  return (
    <article className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/library"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Recovery Library
      </Link>

      <header className="mt-6 rounded-3xl border border-gold/20 bg-gradient-to-br from-card via-card to-gold/5 p-8 sm:p-10">
        <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <span>Guide {String(guide.number).padStart(2, "0")}</span>
          <span className="text-border">·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {guide.readMin} min read
          </span>
        </div>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground sm:text-5xl">
          <span className="me-2" aria-hidden>
            {guide.emoji}
          </span>
          {guide.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {guide.hero.intro}
        </p>

        {guide.hero.highlights && (
          <ul className="mt-5 grid gap-2 sm:grid-cols-2">
            {guide.hero.highlights.map((h) => (
              <li
                key={h}
                className="rounded-xl border border-gold/20 bg-background/60 px-3 py-2 text-sm text-foreground/85"
              >
                {h}
              </li>
            ))}
          </ul>
        )}

        {guide.hero.goal && (
          <div className="mt-5 rounded-xl bg-gold/10 px-4 py-3 text-sm text-foreground">
            <span className="font-semibold text-[var(--gold-deep)]">Goal · </span>
            {guide.hero.goal}
          </div>
        )}

        <div className="mt-5">
          <FavoriteButton
            storageKey="rt:fav-library"
            id={guide.slug}
            label={guide.title}
            sub={`Guide · ${guide.readMin} min read`}
            to={`/library/${guide.slug}`}
            variant="pill"
          />
        </div>
      </header>

      <div className="mt-10 space-y-10">
        {guide.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-display text-2xl text-foreground">{section.heading}</h2>
            {section.intro && (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {section.intro}
              </p>
            )}

            {section.items && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {section.items.map((it) => (
                  <div
                    key={it.title}
                    className="rounded-2xl border border-border/60 bg-card p-4"
                  >
                    <h3 className="font-display text-base text-foreground">{it.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {section.bullets && (
              <ul className="mt-4 space-y-2">
                {section.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex gap-3 rounded-xl border border-border/40 bg-card/60 px-4 py-2.5 text-sm text-foreground/90"
                  >
                    <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <section className="mt-14 border-t border-border/50 pt-8">
        <h2 className="font-display text-xl text-foreground">Keep exploring</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {others.map((g) => (
            <Link
              key={g.slug}
              to="/library/$slug"
              params={{ slug: g.slug }}
              className="group rounded-2xl border border-border/60 bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[var(--shadow-gold)]"
            >
              <div className="text-2xl" aria-hidden>
                {g.emoji}
              </div>
              <div className="mt-2 font-display text-base text-foreground">{g.title}</div>
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{g.blurb}</p>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}

function ResourceView({ resource }: { resource: Resource }) {
  const isMobile = useIsMobile();
  return (
    <article className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/library"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Recovery Library
      </Link>

      <header className="mt-6 rounded-3xl border border-gold/20 bg-gradient-to-br from-card via-card to-gold/5 p-8 sm:p-10">
        <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" />
          <span>{resource.kind}</span>
        </div>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground sm:text-5xl">
          {resource.emoji && (
            <span className="me-2" aria-hidden>
              {resource.emoji}
            </span>
          )}
          {resource.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {resource.body.intro}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <FavoriteButton
            storageKey="rt:fav-library"
            id={resource.slug}
            label={resource.title}
            sub={resource.kind}
            to={`/library/${resource.slug}`}
            variant="pill"
          />
          {resource.href && (
            <>
              <a
                href={resource.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--gold-deep)] px-5 py-2 text-sm font-semibold text-background transition hover:opacity-90"
              >
                <BookOpen className="h-4 w-4" /> Open PDF
              </a>
              {isMobile ? (
                <span className="self-center text-xs text-muted-foreground">
                  Downloads are available on the desktop version.
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void downloadFile(resource.href!, `${resource.slug}.pdf`)}
                  className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card px-5 py-2 text-sm font-semibold text-[var(--gold-deep)] transition hover:border-[var(--gold-deep)] hover:bg-gold-light/30"
                >
                  <Download className="h-4 w-4" /> Download
                </button>
              )}
            </>
          )}
        </div>
        {resource.href && (
          <p className="mt-3 text-xs text-muted-foreground">
            Blocked by your browser?{" "}
            <a
              href={resource.href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[var(--gold-deep)] underline underline-offset-2"
            >
              Open the PDF directly
            </a>{" "}
            or long-press the link to save it.
          </p>
        )}

      </header>

      <div className="mt-10 space-y-10">
        {resource.body.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-display text-2xl text-foreground">{section.heading}</h2>

            {section.paragraphs?.map((p, i) => (
              <p key={i} className="mt-3 text-base leading-relaxed text-foreground/85">
                {p}
              </p>
            ))}

            {section.bullets && (
              <ul className="mt-4 space-y-2">
                {section.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex gap-3 rounded-xl border border-border/40 bg-card/60 px-4 py-2.5 text-sm text-foreground/90"
                  >
                    <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {resource.body.closing && (
          <p className="rounded-2xl bg-gold/10 px-5 py-4 text-base italic leading-relaxed text-foreground">
            {resource.body.closing}
          </p>
        )}
      </div>

      <p className="mt-10 text-xs text-muted-foreground">
        General educational information — not a substitute for therapy or medical care.
      </p>

      <div className="mt-8">
        <Link
          to="/library"
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-gold/40"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Library
        </Link>
      </div>
    </article>
  );
}
